import { useQuery } from '@tanstack/react-query';
import React, { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { HealthcareEntitiesService } from '../../../../js/api/services/HealthcareEntitiesService';
import { createGoogleMapsLink } from '../../../../js/utils/address';
import { Heading } from '../../components/heading';
import { Navbar, NavbarItem } from '../../components/navbar';
import { useCodeDefinitionsByCategories } from '../../hooks/useCodeDefinitions';
import { DynamicFieldFactory } from '../../utils/DynamicFieldFactory';
import {
  FACILITY_CONTACTS_QUERY_KEY,
  FACILITY_QUERY_KEY,
  FACILITY_SPECIALTIES_QUERY_KEY,
} from '../Organization/Facilities/queryKeys';
import { FacilityContactCard } from '../Organization/FacilityContactCard';
import { FacilitySpecialtiesList } from '../Organization/FacilitySpecialtiesList';

// eslint-disable-next-line sonarjs/cognitive-complexity
export const ProviderFacilityDetailsFull: React.FC = () => {
  const { facilityId } = useParams<{ facilityId: string }>();
  const [activeTab, setActiveTab] = React.useState<'details' | 'specialties'>('details');

  const { data: codeDefinitionsByCategory } = useCodeDefinitionsByCategories([
    'FacilityTypes',
    'HospitalOwnershipChoices',
    'EMRChoices',
    'FacilityContactRoles',
    'Specialty',
  ]);

  const enrichDynamicFieldsWithChoices = useCallback(
    (fields: any[]): any[] => {
      if (!fields || !codeDefinitionsByCategory) return [];

      return fields.map((field) => {
        if (field.choices_category) {
          const categoryDefs = codeDefinitionsByCategory[field.choices_category] || [];
          const choices = categoryDefs.map((def) => ({ code: def.code, title: def.title }));
          return { ...field, choices };
        }
        return field;
      });
    },
    [codeDefinitionsByCategory]
  );

  const { data: facilityData, isLoading: isFacilityLoading } = useQuery({
    queryKey: [FACILITY_QUERY_KEY, facilityId],
    queryFn: () =>
      HealthcareEntitiesService.healthcareEntitiesFacilitiesRetrieve({ id: facilityId! }),
    enabled: !!facilityId,
  });

  const { data: specialtiesData } = useQuery({
    queryKey: [FACILITY_SPECIALTIES_QUERY_KEY, facilityId],
    queryFn: () =>
      HealthcareEntitiesService.healthcareEntitiesFacilitySpecialtiesList({
        facility: facilityId!,
      }),
    enabled: !!facilityId,
  });

  const { data: contactsData } = useQuery({
    queryKey: [FACILITY_CONTACTS_QUERY_KEY, facilityId],
    queryFn: () =>
      HealthcareEntitiesService.healthcareEntitiesFacilityContactsList({ facility: facilityId! }),
    enabled: !!facilityId,
  });

  const codeDefMaps = useMemo(() => {
    if (!codeDefinitionsByCategory) return {};
    const maps: any = {};
    Object.entries(codeDefinitionsByCategory).forEach(([category, defs]) => {
      maps[category] = {};
      defs.forEach((def) => {
        maps[category][def.code] = def.title;
      });
    });
    return maps;
  }, [codeDefinitionsByCategory]);

  const facility = useMemo(() => {
    if (!facilityData || !codeDefinitionsByCategory) return null;
    const fields =
      typeof facilityData.facility_type_fields === 'string'
        ? JSON.parse(facilityData.facility_type_fields)
        : facilityData.facility_type_fields || [];
    return {
      ...facilityData,
      facility_type_fields: enrichDynamicFieldsWithChoices(fields),
    };
  }, [facilityData, codeDefinitionsByCategory, enrichDynamicFieldsWithChoices]);

  const specialtyOptions = useMemo(() => {
    if (!codeDefinitionsByCategory) return [];
    const specialtyDefs = codeDefinitionsByCategory.Specialty || [];
    return specialtyDefs.map((opt) => ({
      value: opt.code,
      label: opt.title,
    }));
  }, [codeDefinitionsByCategory]);

  const contacts = useMemo(() => {
    return contactsData?.results || [];
  }, [contactsData]);

  const attachedSpecialties = useMemo(() => {
    if (!specialtiesData?.results || !codeDefinitionsByCategory) return [];
    return specialtiesData.results.map((specialty) => ({
      ...specialty,
      dynamic_fields: enrichDynamicFieldsWithChoices(
        typeof specialty.dynamic_fields === 'string'
          ? JSON.parse(specialty.dynamic_fields)
          : specialty.dynamic_fields
      ),
    }));
  }, [specialtiesData, codeDefinitionsByCategory, enrichDynamicFieldsWithChoices]);

  if (isFacilityLoading || !facility) {
    return <div className="tw:p-8 tw:text-center">Loading...</div>;
  }

  return (
    <div className="tw:mt-4 tw:max-w-4xl tw:mx-auto tw:mb-12">
      <div className="tw:bg-white tw:rounded-lg tw:shadow tw:dark:bg-zinc-700 tw:dark:border tw:dark:border-zinc-400">
        {/* Org/Facility Name Row */}
        <div className="tw:flex tw:flex-col tw:gap-1 tw:mb-4 tw:px-8 tw:pt-8">
          <h1 className="tw:!text-brand-green-0 tw:!mb-0 tw:text-2xl tw:font-bold tw:dark:!text-brand-green-1">
            {facility.name}
          </h1>
          <div className="tw:flex tw:items-center tw:text-sm tw:gap-4 tw:text-zinc-400">
            <span className="tw:flex tw:items-center tw:gap-2">
              <i className="fa-light fa-hospital" />
              <span>
                {codeDefMaps['FacilityTypes']?.[facility.facility_type] || facility.facility_type}
              </span>
            </span>
            <span className="tw:flex tw:items-center tw:gap-2">
              <i className="fa-light fa-location-dot" />
              <span>
                {facility.city ? `${facility.city}, ` : ''}{' '}
                {facility.state ? `${facility.state}` : ''}
                {!facility.city && !facility.state ? '--' : ''}
              </span>
            </span>
          </div>
        </div>
        {/* Navbar */}
        <Navbar className="tw:mb-0 tw:pl-6 tw:border-t tw:border-b tw:border-zinc-200 tw:dark:border-zinc-400">
          <NavbarItem
            type="button"
            current={activeTab === 'details'}
            onClick={() => setActiveTab('details')}
          >
            Facility Details
          </NavbarItem>
          <NavbarItem
            type="button"
            current={activeTab === 'specialties'}
            onClick={() => setActiveTab('specialties')}
          >
            Specialties
          </NavbarItem>
        </Navbar>
        {/* Tab Content */}
        {activeTab === 'details' && (
          <>
            {/* Facility Details Section */}
            <section className="tw:px-8 tw:pt-8 tw:mb-4">
              <div className="tw:mb-12">
                <div className="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-x-8 tw:gap-y-6">
                  {/* Column 1 */}
                  <div className="tw:space-y-6">
                    {/* Address */}
                    <div className="tw:flex tw:items-start tw:justify-between">
                      {/* Address label and data */}
                      <div>
                        <div className="tw:text-zinc-400 tw:text-sm tw:flex tw:items-center tw:gap-2">
                          Address
                          {facility.street_address && (
                            <a
                              href={createGoogleMapsLink(
                                facility.street_address,
                                facility.city,
                                facility.state,
                                facility.zip_code
                              )}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="Open in Google Maps"
                            >
                              <i className="fa-solid fa-location-dot" />
                            </a>
                          )}
                        </div>
                        <div className="tw:text-zinc-700 tw:dark:text-zinc-300">
                          {facility.street_address && <div>{facility.street_address}</div>}
                          {facility.street_address2 && <div>{facility.street_address2}</div>}
                          {(facility.city || facility.state || facility.zip_code) && (
                            <div>
                              {facility.city}
                              {facility.city && facility.state ? ', ' : ''}
                              {facility.state}
                              {(facility.city || facility.state) && facility.zip_code ? ' ' : ''}
                              {facility.zip_code}
                            </div>
                          )}
                          {!facility.street_address &&
                            !facility.street_address2 &&
                            !facility.city &&
                            !facility.state &&
                            !facility.zip_code && <div>--</div>}
                          <div>
                            {facility.phone_number ? (
                              <a
                                href={`tel:${facility.phone_number}`}
                                className="tw:hover:underline"
                                title={`Call ${facility.phone_number}`}
                              >
                                {facility.phone_number}
                              </a>
                            ) : (
                              '--'
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Column 2 */}
                  <div className="tw:space-y-6">
                    <div>
                      <div className="tw:text-zinc-400 tw:text-sm">Ownership</div>
                      <div className="tw:text-zinc-700 tw:dark:text-zinc-300">
                        {(facility.hospital_ownership &&
                          codeDefMaps['HospitalOwnershipChoices']?.[facility.hospital_ownership]) ||
                          '--'}
                      </div>
                    </div>
                    <div>
                      <div className="tw:text-zinc-400 tw:text-sm">EMR System</div>
                      <div className="tw:text-zinc-700 tw:dark:text-zinc-300">
                        {(facility.emr && codeDefMaps['EMRChoices']?.[facility.emr]) || '--'}
                      </div>
                    </div>
                  </div>
                </div>
                {facility.extra_info && (
                  <div className="tw:mt-4">
                    <div className="tw:text-zinc-400 tw:text-sm">Additional Info</div>
                    <div className="tw:text-zinc-700 tw:dark:text-zinc-300 tw:mt-1 tw:whitespace-pre-line">
                      {facility.extra_info}
                    </div>
                  </div>
                )}
                {facility.facility_type_fields && facility.facility_type_fields.length > 0 && (
                  <div className="tw:mt-4">
                    <DynamicFieldFactory
                      fields={facility.facility_type_fields}
                      columns={2}
                      onChange={() => {}}
                      readOnly={true}
                    />
                  </div>
                )}
              </div>
            </section>
            {/* Contacts */}
            <section className="tw:px-8 tw:pb-8">
              <div className="tw:flex tw:items-center tw:gap-2 tw:mb-4">
                <i className="fa-solid fa-user-group tw:text-brand-green-0 tw:dark:text-brand-green-1" />
                <Heading level={3} className="tw:!mb-0 tw:dark:!text-brand-green-1 ">
                  Contacts
                </Heading>
              </div>
              <div className="tw:grid tw:grid-cols-1 tw:gap-4">
                {contacts.length > 0 ? (
                  contacts.map((facilityContact: any) => (
                    <FacilityContactCard
                      key={facilityContact.id}
                      facilityContact={facilityContact}
                      canEdit={false}
                      roleMap={codeDefMaps['FacilityContactRoles']}
                    />
                  ))
                ) : (
                  <div className="tw:text-zinc-500 tw:dark:text-zinc-400">No contacts yet.</div>
                )}
              </div>
            </section>
          </>
        )}
        {activeTab === 'specialties' && (
          <>
            {/* Specialties */}
            <section className="tw:px-8 tw:pt-8 tw:pb-8">
              <div className="tw:flex tw:items-center tw:mb-4">
                <Heading level={3} className="tw:!mb-0 tw:dark:!text-brand-green-1">
                  <i className="fa-regular fa-stethoscope tw:text-brand-green-0 tw:mr-2 tw:dark:text-brand-green-1" />
                  Specialty Support
                </Heading>
              </div>
              <FacilitySpecialtiesList
                facilityId={facilityId!}
                specialtyOptions={specialtyOptions}
                specialties={attachedSpecialties}
                onUpdate={() => {}}
                canEdit={false}
              />
            </section>
          </>
        )}
      </div>
    </div>
  );
};
